// skills/woodworking/woodworkingUI.js
// Woodworking UI - Crafting-style card layout with tabs

import { woodworkingSkill } from './woodworking.js';
import { SHAFT_RECIPES, ARROW_RECIPES, BOW_RECIPES, STAFF_RECIPES, TOOLS } from './woodworkingData.js';
import { gameState } from '../../game/gameState.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { IS_DEV } from '../../config/env.js';
import { adminPanel } from '../admin/admin.js';
import { getCurrentSkillId } from '../../core/routeSetup.js';
import { formatWithCommas } from '../../utils/format.js';
import { getXPPerHour } from '../../systems/experience.js';
import { getSkillProgressInfo, updateCompactHeaderProgress } from '../../ui/skillProgress.js';
import { getItemName, getItemIcon } from '../../data/items/index.js';
import { getCorruptionSpeedMultiplier, getCorruptionLootMultiplier, getCorruptionXPMultiplier } from '../../systems/corruption.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { updateToolUpgradeCost } from '../../ui/uiHelpers.js';
import { renderSkillInfo } from '../../ui/skillInfo.js';
import { buildEquipmentStatsTooltip } from '../../ui/equipmentTooltip.js';
import { renderTalentButton, isTalentTreeOpen } from '../../systems/talentTree/talentTreeUI.js';
import { getTalentXPBonus, getTalentFlatBonus } from '../../systems/talentTree/talentTree.js';
import { getSkillIcon } from '../../core/skillRegistry.js';
import { getAtlasPercentBg } from '../../data/atlasManifest.js';

// Track loot multiplier to detect corruption changes
let lastLootMultiplier = 1;

// Current tab state
let currentTab = 'shafts';

// Arrow shaft icon for card headers (lazy — atlas loads after module import)
let _shaftSpriteCache = null;
function getShaftIconSprite() {
    if (_shaftSpriteCache) return _shaftSpriteCache;
    const sheet = 'assets/icons/woodworking/arrow_shaft_5.png';
    const atlas = getAtlasPercentBg(sheet);
    _shaftSpriteCache = atlas
        ? `<span class="sprite-icon sprite-icon-lg" style="background-image: url('${atlas.atlas}'); background-size: ${atlas.bgSize}; background-position: ${atlas.bgPosition}; width: 64px; height: 64px;"></span>`
        : `<span class="sprite-icon sprite-icon-lg" style="background-image: url('${sheet}'); background-position: 0px 0px; width: 64px; height: 64px; background-size: contain;"></span>`;
    return _shaftSpriteCache;
}

/**
 * Render compact header with knife upgrades
 */
function renderCompactHeader() {
    const skill = gameState.getSkill(SKILLS.WOODWORKING);
    const xpPerHour = getXPPerHour(SKILLS.WOODWORKING);
    const { levelDisplay, progressPercent, progressText } = getSkillProgressInfo(SKILLS.WOODWORKING);

    // Tool info
    const activeTool = woodworkingSkill.getActiveBuff();
    const tools = Object.values(TOOLS);
    const currentIndex = tools.findIndex(t => t.id === activeTool.id);
    const nextTool = tools[currentIndex + 1];
    const playerGold = gameState.getGold();

    let upgradeHtml = '';
    if (nextTool) {
        const canAfford = playerGold >= nextTool.price;
        if (skill.level < nextTool.level) {
            upgradeHtml = `
                <span class="compact-upgrade locked">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${canAfford ? 'affordable' : 'cant-afford'}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                    <span class="upgrade-lock">🔒 ${skill.level}/${nextTool.level}</span>
                </span>
            `;
        } else {
            const priceClass = canAfford ? 'affordable' : 'cant-afford';
            upgradeHtml = `
                <span class="compact-upgrade available" onclick="window.woodworkingUI.upgradeTool('${nextTool.id}')">
                    → ${nextTool.name} (${nextTool.speedMultiplier}x)
                    <span class="upgrade-cost ${priceClass}" data-price="${nextTool.price}">${getItemIcon('coins')} ${formatWithCommas(nextTool.price)}</span>
                </span>
            `;
        }
    } else {
        upgradeHtml = `<span class="compact-upgrade maxed">✓ Max</span>`;
    }

    return `
        <div class="compact-header">
            <div class="compact-header-row">
                <div class="compact-title">
                    <span class="compact-icon">${getSkillIcon(SKILLS.WOODWORKING)}</span>
                    <span class="compact-name">Woodworking</span>
                    ${renderSkillInfo(SKILLS.WOODWORKING)}
                    <span class="compact-level">Lv ${levelDisplay}</span>
                    ${renderTalentButton(SKILLS.WOODWORKING)}
                </div>
                <div class="compact-stats">
                    <span class="compact-stat xp-total">
                        <span class="stat-label">Total XP:</span>
                        <span class="stat-value">${formatWithCommas(skill.xp)}</span>
                    </span>
                    <span class="compact-stat xp-rate">
                        <span class="stat-label">XP/h:</span>
                        <span class="stat-value">${formatWithCommas(xpPerHour)}</span>
                    </span>
                </div>
                <div class="compact-tool">
                    <span class="tool-label">Knife:</span>
                    <span class="tool-current">${activeTool.name} (${activeTool.speedMultiplier}x)</span>
                    ${upgradeHtml}
                </div>
            </div>
            <div class="compact-progress">
                <div class="compact-progress-bar">
                    <div class="compact-progress-fill" style="width: ${progressPercent}%"></div>
                    <span class="compact-progress-percent">${progressPercent}%</span>
                </div>
                <span class="compact-progress-text">${progressText} XP</span>
            </div>
        </div>
    `;
}

/**
 * Render tab navigation
 */
function renderTabs() {
    return `
        <div class="skill-tabs">
            <button class="skill-tab ${currentTab === 'shafts' ? 'active' : ''}"
                    onclick="window.woodworkingUI.switchTab('shafts')">
                <span class="tab-bar-icon silhouette">${getItemIcon('arrow_shafts', 1000)}</span>Shafts
            </button>
            <button class="skill-tab ${currentTab === 'arrows' ? 'active' : ''}"
                    onclick="window.woodworkingUI.switchTab('arrows')">
                <span class="tab-bar-icon silhouette">${getItemIcon('pyrestone_arrows')}</span>Arrows
            </button>
            <button class="skill-tab ${currentTab === 'bows' ? 'active' : ''}"
                    onclick="window.woodworkingUI.switchTab('bows')">
                <span class="tab-bar-icon silhouette">${getItemIcon('shortbow')}</span>Bows
            </button>
            <button class="skill-tab ${currentTab === 'staffs' ? 'active' : ''}"
                    onclick="window.woodworkingUI.switchTab('staffs')">
                <span class="tab-bar-icon silhouette">${getItemIcon('common_staff')}</span>Staffs
            </button>
        </div>
    `;
}

/**
 * Render a shaft recipe card (single input)
 */
function renderShaftCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === 'woodworking' && currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time using centralized speed calculation
    const speedMultiplier = woodworkingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.WOODWORKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.WOODWORKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses
    const talentXPBonus = getTalentXPBonus(SKILLS.WOODWORKING);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Output multiplier (Bulk Production shaft bonus + corruption loot)
    const corruptionLootMult = getCorruptionLootMultiplier(SKILLS.WOODWORKING);
    const shaftBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'shaftBonus');

    // Check materials
    const playerCount = gameState.getItemCount(recipe.inputItem);
    const hasMaterials = playerCount >= recipe.inputQuantity;
    const inputIcon = getItemIcon(recipe.inputItem);
    const inputName = getItemName(recipe.inputItem);

    // Output (apply shaft bonus + corruption loot)
    const drop = recipe.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const adjustedQty = shaftBonus > 0 ? Math.floor(baseQty * (1 + shaftBonus)) : baseQty;
    const effectiveQty = adjustedQty * corruptionLootMult;
    const isBoosted = effectiveQty > baseQty;
    const qtyDisplay = isBoosted
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'ww-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-recipe-id="${recipe.id}"
             style="--recipe-color: ${recipe.color}">

            <div class="ww-card-header">
                <span class="ww-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="ww-card-icon">${getShaftIconSprite()}</span>
                <span class="ww-card-name">${recipe.name}</span>
            </div>

            <div class="ww-card-info">
                <div class="ww-stat">
                    <span class="stat-icon woodworking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="ww-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="ww-materials">
                <div class="materials-label">Input</div>
                <div class="material-row ${!hasMaterials ? 'missing' : ''}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${playerCount}/${recipe.inputQuantity}</span>
                </div>
            </div>

            <div class="ww-output">
                <div class="output-label">Output</div>
                <div class="output-row">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="ww-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.woodworkingUI.toggleMakingShafts('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Make'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="ww-progress">
                <div class="ww-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render an arrow recipe card (multiple inputs)
 */
function renderArrowCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === 'woodworking' && currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time using centralized speed calculation
    const speedMultiplier = woodworkingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.WOODWORKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.WOODWORKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses
    const talentXPBonus = getTalentXPBonus(SKILLS.WOODWORKING);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier
    const lootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODWORKING);

    // Check all materials
    let hasMaterials = true;
    const materialsHtml = recipe.inputItems.map(input => {
        const playerCount = gameState.getItemCount(input.itemId);
        const hasEnough = playerCount >= input.quantity;
        if (!hasEnough) hasMaterials = false;
        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);
        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${playerCount}/${input.quantity}</span>
            </div>
        `;
    }).join('');

    // Output
    const drop = recipe.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const effectiveQty = baseQty * lootMultiplier;
    const qtyDisplay = lootMultiplier > 1
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'ww-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-recipe-id="${recipe.id}"
             style="--recipe-color: ${recipe.color}">

            <div class="ww-card-header">
                <span class="ww-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="ww-card-icon">${outputIcon}</span>
                <span class="ww-card-name">${recipe.name}</span>
            </div>

            <div class="ww-card-info">
                <div class="ww-stat">
                    <span class="stat-icon woodworking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="ww-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="ww-materials">
                <div class="materials-label">Input</div>
                ${materialsHtml}
            </div>

            <div class="ww-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(recipe.drops[0].itemId)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="ww-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.woodworkingUI.toggleMakingArrows('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Make'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="ww-progress">
                <div class="ww-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a bow recipe card (single input)
 */
function renderBowCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === 'woodworking' && currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time using centralized speed calculation
    const speedMultiplier = woodworkingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.WOODWORKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.WOODWORKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses
    const talentXPBonus = getTalentXPBonus(SKILLS.WOODWORKING);
    let effectiveXP = recipe.xp;
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier
    const lootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODWORKING);

    // Check materials
    const playerCount = gameState.getItemCount(recipe.inputItem);
    const hasMaterials = playerCount >= recipe.inputQuantity;
    const inputIcon = getItemIcon(recipe.inputItem);
    const inputName = getItemName(recipe.inputItem);

    // Output
    const drop = recipe.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const effectiveQty = baseQty * lootMultiplier;
    const qtyDisplay = lootMultiplier > 1
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'ww-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-recipe-id="${recipe.id}"
             style="--recipe-color: ${recipe.color}">

            <div class="ww-card-header">
                <span class="ww-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="ww-card-icon">${outputIcon}</span>
                <span class="ww-card-name">${recipe.name}</span>
            </div>

            <div class="ww-card-info">
                <div class="ww-stat">
                    <span class="stat-icon woodworking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="ww-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="ww-materials">
                <div class="materials-label">Input</div>
                <div class="material-row ${!hasMaterials ? 'missing' : ''}">
                    <span class="material-icon">${inputIcon}</span>
                    <span class="material-name">${inputName}</span>
                    <span class="material-count">${playerCount}/${recipe.inputQuantity}</span>
                </div>
            </div>

            <div class="ww-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(recipe.drops[0].itemId)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="ww-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.woodworkingUI.toggleMakingBow('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Make'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="ww-progress">
                <div class="ww-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render a staff recipe card (multiple inputs)
 */
function renderStaffCard(recipe, playerLevel, currentAction) {
    const isUnlocked = playerLevel >= recipe.level;
    const isActive = currentAction?.type === 'woodworking' && currentAction?.recipe?.id === recipe.id;
    const progress = isActive ? currentAction.progress : 0;

    // Calculate effective time using centralized speed calculation
    const speedMultiplier = woodworkingSkill.getEffectiveSpeedMultiplier();
    const corruptionSpeedMult = getCorruptionSpeedMultiplier(SKILLS.WOODWORKING);
    const corruptionXPMult = getCorruptionXPMultiplier(SKILLS.WOODWORKING);
    const totalSpeedMultiplier = speedMultiplier * corruptionSpeedMult;
    const effectiveTime = recipe.baseTime / totalSpeedMultiplier;

    // Calculate effective XP with talent and corruption bonuses (includes Arcane Sensitivity for staffs)
    const talentXPBonus = getTalentXPBonus(SKILLS.WOODWORKING);
    const staffXpBonus = getTalentFlatBonus(SKILLS.WOODWORKING, 'staffXpBonus');
    let effectiveXP = recipe.xp;
    if (staffXpBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + staffXpBonus));
    if (corruptionXPMult > 1) effectiveXP = Math.floor(effectiveXP * corruptionXPMult);
    if (talentXPBonus > 0) effectiveXP = Math.floor(effectiveXP * (1 + talentXPBonus));

    // Loot multiplier
    const lootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODWORKING);

    // Check all materials
    let hasMaterials = true;
    const materialsHtml = recipe.inputItems.map(input => {
        const playerCount = gameState.getItemCount(input.itemId);
        const hasEnough = playerCount >= input.quantity;
        if (!hasEnough) hasMaterials = false;
        const inputIcon = getItemIcon(input.itemId);
        const inputName = getItemName(input.itemId);
        return `
            <div class="material-row ${!hasEnough ? 'missing' : ''}">
                <span class="material-icon">${inputIcon}</span>
                <span class="material-name">${inputName}</span>
                <span class="material-count">${playerCount}/${input.quantity}</span>
            </div>
        `;
    }).join('');

    // Output
    const drop = recipe.drops[0];
    const outputIcon = getItemIcon(drop.itemId);
    const outputName = getItemName(drop.itemId);
    const baseQty = drop.quantity;
    const effectiveQty = baseQty * lootMultiplier;
    const qtyDisplay = lootMultiplier > 1
        ? `<span class="output-qty boosted">x${effectiveQty}</span>`
        : `<span class="output-qty">x${baseQty}</span>`;

    const cardClasses = [
        'ww-card',
        isActive ? 'active' : '',
        !isUnlocked ? 'locked' : '',
        !hasMaterials && isUnlocked ? 'no-materials' : ''
    ].filter(Boolean).join(' ');

    return `
        <div class="${cardClasses}"
             data-recipe-id="${recipe.id}"
             style="--recipe-color: ${recipe.color}">

            <div class="ww-card-header">
                <span class="ww-card-level">${isUnlocked ? `Lvl ${recipe.level}` : `Req: ${recipe.level}`}</span>
                <span class="ww-card-icon">${outputIcon}</span>
                <span class="ww-card-name">${recipe.name}</span>
            </div>

            <div class="ww-card-info">
                <div class="ww-stat">
                    <span class="stat-icon woodworking-icon"></span>
                    <span class="stat-label">XP:</span>
                    <span class="stat-value xp ${corruptionXPMult > 1 ? 'boosted' : ''}">${effectiveXP}</span>
                </div>
                <div class="ww-stat">
                    <span class="stat-icon time-icon"></span>
                    <span class="stat-label">Time:</span>
                    <span class="stat-value ${corruptionSpeedMult > 1 ? 'boosted' : ''}">${(effectiveTime / 1000).toFixed(1)}s</span>
                </div>
            </div>

            <div class="ww-materials">
                <div class="materials-label">Input</div>
                ${materialsHtml}
            </div>

            <div class="ww-output">
                <div class="output-label">Output</div>
                <div class="output-row output-hoverable" data-tooltip="${buildEquipmentStatsTooltip(recipe.drops[0].itemId)}">
                    <span class="output-icon">${outputIcon}</span>
                    <span class="output-name">${outputName}</span>
                    ${qtyDisplay}
                </div>
            </div>

            <div class="ww-action">
                ${isUnlocked ? `
                    <button class="action-btn ${isActive ? 'active' : ''}"
                            onclick="window.woodworkingUI.toggleMakingStaff('${recipe.id}')">
                        ${isActive ? 'Stop' : 'Make'}
                    </button>
                ` : `
                    <span class="locked-text">🔒 Locked</span>
                `}
            </div>

            <div class="ww-progress">
                <div class="ww-progress-fill" style="width: ${progress * 100}%"></div>
            </div>
        </div>
    `;
}

/**
 * Render the woodworking interface
 */
export function renderWoodworkingUI(params = {}) {
    if (params.tab) {
        currentTab = params.tab;
    }

    const container = document.getElementById('skill-content');
    const skill = gameState.getSkill(SKILLS.WOODWORKING);
    const currentAction = gameState.getCurrentAction();

    // Update loot multiplier tracker
    lastLootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODWORKING);

    let cardsHtml = '';
    switch (currentTab) {
        case 'arrows':
            cardsHtml = Object.values(ARROW_RECIPES)
                .map(recipe => renderArrowCard(recipe, skill.level, currentAction))
                .join('');
            break;
        case 'bows':
            cardsHtml = Object.values(BOW_RECIPES)
                .map(recipe => renderBowCard(recipe, skill.level, currentAction))
                .join('');
            break;
        case 'staffs':
            cardsHtml = Object.values(STAFF_RECIPES)
                .map(recipe => renderStaffCard(recipe, skill.level, currentAction))
                .join('');
            break;
        default:
            cardsHtml = Object.values(SHAFT_RECIPES)
                .map(recipe => renderShaftCard(recipe, skill.level, currentAction))
                .join('');
    }

    container.innerHTML = `
        <div class="woodworking-interface">
            ${renderCompactHeader()}
            ${renderTabs()}
            <div class="ww-grid">
                ${cardsHtml}
            </div>
        </div>
    `;
}

export function switchTab(tab) {
    currentTab = tab;
    renderWoodworkingUI();
}

export function toggleMakingShafts(shaftId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.recipe?.id === shaftId) {
        woodworkingSkill.stopMakingShafts();
    } else {
        const result = woodworkingSkill.canMakeShafts(shaftId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            woodworkingSkill.startMakingShafts(shaftId);
        }
    }

    renderWoodworkingUI();
}

export function toggleMakingArrows(arrowId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.recipe?.id === arrowId) {
        woodworkingSkill.stopMakingArrows();
    } else {
        const result = woodworkingSkill.canMakeArrows(arrowId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            woodworkingSkill.startMakingArrows(arrowId);
        }
    }

    renderWoodworkingUI();
}

export function toggleMakingBow(bowId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.recipe?.id === bowId) {
        woodworkingSkill.stopMakingBow();
    } else {
        const result = woodworkingSkill.canMakeBow(bowId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            woodworkingSkill.startMakingBow(bowId);
        }
    }

    renderWoodworkingUI();
}

export function toggleMakingStaff(staffId) {
    const currentAction = gameState.getCurrentAction();

    if (currentAction && currentAction.recipe?.id === staffId) {
        woodworkingSkill.stopMakingStaff();
    } else {
        const result = woodworkingSkill.canMakeStaff(staffId);
        if (!result.success) {
            toastWarning(result.reason);
        } else {
            woodworkingSkill.startMakingStaff(staffId);
        }
    }

    renderWoodworkingUI();
}

export function upgradeTool(toolId) {
    const result = woodworkingSkill.upgradeTool(toolId);

    if (result.success) {
        renderWoodworkingUI();
    } else {
        toastWarning(result.reason);
    }
}

export function updateWoodworkingUI() {
    // Check if loot multiplier changed (corruption started/ended)
    const currentLootMultiplier = getCorruptionLootMultiplier(SKILLS.WOODWORKING);
    if (currentLootMultiplier !== lastLootMultiplier) {
        lastLootMultiplier = currentLootMultiplier;
        renderWoodworkingUI();
        return;
    }

    // Update compact header stats
    const skill = gameState.getSkill(SKILLS.WOODWORKING);
    const xpPerHour = getXPPerHour(SKILLS.WOODWORKING);
    const { levelDisplay } = getSkillProgressInfo(SKILLS.WOODWORKING);

    const xpTotalEl = document.querySelector('.woodworking-interface .compact-stat.xp-total .stat-value');
    if (xpTotalEl) xpTotalEl.textContent = formatWithCommas(skill.xp);

    const xpRateEl = document.querySelector('.woodworking-interface .compact-stat.xp-rate .stat-value');
    if (xpRateEl) xpRateEl.textContent = formatWithCommas(xpPerHour);

    const levelEl = document.querySelector('.woodworking-interface .compact-level');
    if (levelEl) levelEl.textContent = `Lv ${levelDisplay}`;

    updateCompactHeaderProgress('.woodworking-interface', SKILLS.WOODWORKING);

    // Update tool upgrade gold color
    updateToolUpgradeCost('.woodworking-interface');

    // Update material counts on all visible cards
    updateMaterialCounts();

    // Check for stale active cards
    const action = gameState.getCurrentAction();
    const isWoodworkingAction = action?.type === 'woodworking';

    const existingActiveCards = document.querySelectorAll('.woodworking-interface .ww-card.active');
    if (existingActiveCards.length > 0 && !isWoodworkingAction) {
        renderWoodworkingUI();
        return;
    }

    // Update action progress
    if (!isWoodworkingAction) return;

    const activeCard = document.querySelector(`.ww-card[data-recipe-id="${action.recipe.id}"]`);
    if (activeCard) {
        const cardProgressFill = activeCard.querySelector('.ww-progress-fill');
        if (cardProgressFill) {
            cardProgressFill.style.width = `${action.progress * 100}%`;
        }
    }
}

/**
 * Update material counts on all visible cards
 */
function updateMaterialCounts() {
    const cards = document.querySelectorAll('.woodworking-interface .ww-card');

    cards.forEach(card => {
        const recipeId = card.dataset.recipeId;
        if (!recipeId) return;

        // Find the recipe in current tab's data
        let recipe = null;
        let isMultiInput = false;

        if (currentTab === 'shafts') {
            recipe = Object.values(SHAFT_RECIPES).find(r => r.id === recipeId);
        } else if (currentTab === 'arrows') {
            recipe = Object.values(ARROW_RECIPES).find(r => r.id === recipeId);
            isMultiInput = true;
        } else if (currentTab === 'bows') {
            recipe = Object.values(BOW_RECIPES).find(r => r.id === recipeId);
        } else if (currentTab === 'staffs') {
            recipe = Object.values(STAFF_RECIPES).find(r => r.id === recipeId);
            isMultiInput = true;
        }

        if (!recipe) return;

        if (isMultiInput) {
            // Arrows have multiple inputs
            const materialRows = card.querySelectorAll('.material-row');
            let allHaveMaterials = true;

            recipe.inputItems.forEach((input, index) => {
                const row = materialRows[index];
                if (!row) return;

                const playerCount = gameState.getItemCount(input.itemId);
                const hasEnough = playerCount >= input.quantity;
                if (!hasEnough) allHaveMaterials = false;

                const countEl = row.querySelector('.material-count');
                if (countEl) countEl.textContent = `${playerCount}/${input.quantity}`;

                row.classList.toggle('missing', !hasEnough);
            });

            card.classList.toggle('no-materials', !allHaveMaterials && !card.classList.contains('locked'));
        } else {
            // Single input (shafts/bows)
            const playerCount = gameState.getItemCount(recipe.inputItem);
            const hasMaterials = playerCount >= recipe.inputQuantity;

            const countEl = card.querySelector('.material-count');
            if (countEl) countEl.textContent = `${playerCount}/${recipe.inputQuantity}`;

            const materialRow = card.querySelector('.material-row');
            if (materialRow) materialRow.classList.toggle('missing', !hasMaterials);

            card.classList.toggle('no-materials', !hasMaterials && !card.classList.contains('locked'));
        }
    });
}

export function initWoodworkingUI() {
    woodworkingSkill.initializeStartingTool();

    woodworkingSkill.onShaftsComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Woodworking level ${xpResult.newLevel}!`);
        }
        if (getCurrentSkillId() === SKILLS.WOODWORKING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderWoodworkingUI();
            }
        }
    };

    woodworkingSkill.onArrowsComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Woodworking level ${xpResult.newLevel}!`);
        }
        if (getCurrentSkillId() === SKILLS.WOODWORKING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderWoodworkingUI();
            }
        }
    };

    woodworkingSkill.onBowComplete = (recipe, xpResult) => {
        if (xpResult.leveledUp) {
            toastSuccess(`Woodworking level ${xpResult.newLevel}!`);
        }
        if (getCurrentSkillId() === SKILLS.WOODWORKING && !isTalentTreeOpen()) {
            const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
            if (speedMult <= 1) {
                renderWoodworkingUI();
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.woodworkingUI = {
        toggleMakingShafts,
        toggleMakingArrows,
        toggleMakingBow,
        toggleMakingStaff,
        upgradeTool,
        switchTab
    };
}

export default {
    renderWoodworkingUI,
    toggleMakingShafts,
    toggleMakingArrows,
    toggleMakingBow,
    toggleMakingStaff,
    upgradeTool,
    updateWoodworkingUI,
    initWoodworkingUI,
    switchTab
};
